import type { EmailAddress, Message, TicketDetail } from "../types";
import { absoluteTime } from "../lib/time";

/**
 * The whole conversation, laid out as a document rather than as a screen.
 *
 * Printing a ticket is how a thread leaves Pulse: into a case file, an
 * escalation to a supplier, a bundle for a dispute. What that needs is the
 * opposite of what the reading view needs -- everything at once, in order, with
 * the headers spelled out, and nothing that only makes sense with a mouse.
 *
 * ## Why a second rendering rather than print styles over the first
 *
 * The thread on screen is three panes of nested `overflow` with a virtualised
 * middle, opened collapsed to its last three messages, and full of controls
 * that mean nothing on paper. Print CSS can hide the controls; it cannot make a
 * scroll container paginate, and it cannot un-collapse a thread whose earlier
 * messages are not in the DOM. Every attempt to print that DOM produces one
 * page of the visible window and stops -- which is the bug this replaces, not a
 * thing to style around.
 *
 * So this is built from the same query, ignores every piece of view state, and
 * lives in a subtree the screen never shows. The trade is one extra rendering
 * of a thread while the print dialog is open, which is why the caller mounts it
 * on demand rather than keeping it there.
 *
 * ## Downloading
 *
 * There is no separate download. Every browser's print dialog has "Save as
 * PDF" as a destination, and it produces a better file than anything we could
 * generate here -- selectable text, the reader's own paper size, no new
 * dependency and no round trip. A second button that produced a worse PDF
 * would be a second button that produced a worse PDF.
 */
export function PrintableThread({
  detail,
  includeNotes,
}: {
  detail: TicketDetail;
  /**
   * Whether internal notes are on the paper.
   *
   * Off by default at the call site, and that is the whole reason this is a
   * parameter. The commonest thing done with a printed thread is showing it to
   * somebody outside the company, and an internal note is by definition the
   * part of a ticket written on the understanding that they will not see it.
   */
  includeNotes: boolean;
}) {
  const { ticket, channel, messages } = detail;

  const shown = includeNotes
    ? messages
    : messages.filter((message) => message.kind !== "note");

  return (
    <div className="print-thread">
      <header className="print-head">
        <h1>{ticket.subject}</h1>
        <dl>
          <Fact label="Ticket">#{ticket.id}</Fact>
          <Fact label="Status">{ticket.status}</Fact>
          <Fact label="Contact">
            {ticket.contactName || ticket.contactEmail || "Unknown"}
            {ticket.contactName && ticket.contactEmail
              ? ` <${ticket.contactEmail}>`
              : ""}
          </Fact>
          {ticket.companyName ? (
            <Fact label="Company">{ticket.companyName}</Fact>
          ) : null}
          <Fact label="Channel">{channel.address}</Fact>
          <Fact label="Last activity">{absoluteTime(ticket.lastMessageAt)}</Fact>
          {/* Dated, because a printed thread outlives the day it was printed
              and the reader has no other way to know how current it is. */}
          <Fact label="Printed">{absoluteTime(new Date().toISOString())}</Fact>
        </dl>
        {!includeNotes && messages.some((m) => m.kind === "note") ? (
          <p className="print-omission">
            Internal notes on this ticket have been left out.
          </p>
        ) : null}
      </header>

      {shown.length === 0 ? (
        <p>No messages on this ticket.</p>
      ) : (
        <ol className="print-messages">
          {shown.map((message) => (
            <li key={message.id}>
              <PrintedMessage message={message} />
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

/** "Name <address>", or whichever half there is. */
function addressLine(people: EmailAddress[] | null): string {
  if (!people?.length) return "";
  return people
    .map((person) => (person.name ? `${person.name} <${person.email}>` : person.email))
    .join(", ");
}

function PrintedMessage({ message }: { message: Message }) {
  const isNote = message.kind === "note";

  /*
   * Who sent it, in the terms the medium uses. An inbound email has a From
   * header; an outbound one was written by an agent whose name is the only
   * useful answer; an automatic message has neither, and saying so is better
   * than attributing it to the desk.
   */
  const from = isNote
    ? (message.authorName ?? "Internal note")
    : (message.fromName ??
      message.fromEmail ??
      message.fromPhone ??
      message.authorName ??
      "Pulse");

  const to = addressLine(message.toRecipients);
  const cc = addressLine(message.ccRecipients);

  return (
    <article className={isNote ? "print-message print-note" : "print-message"}>
      {isNote ? (
        <p className="print-note-flag">
          Internal note — not sent to the customer
        </p>
      ) : null}

      <dl className="print-headers">
        <Fact label="From">{from}</Fact>
        {to ? <Fact label="To">{to}</Fact> : null}
        {cc ? <Fact label="Cc">{cc}</Fact> : null}
        {message.subject && !isNote ? (
          <Fact label="Subject">{message.subject}</Fact>
        ) : null}
        <Fact label="Date">{absoluteTime(message.sentAt)}</Fact>
      </dl>

      {/*
        The same HTML the conversation view draws, sanitised by the API before
        it ever reached this browser -- see the note in MessageBubble. Rendered
        with the quoted thread underneath rather than behind a disclosure: on
        paper there is nothing to click, and a printed thread that silently
        dropped the quoted history would be a worse record than the screen.
      */}
      {message.bodyHtml ? (
        <div
          className="email-body print-body"
          dangerouslySetInnerHTML={{ __html: message.bodyHtml }}
        />
      ) : (
        <p className="print-body">{message.bodyText ?? "(no content)"}</p>
      )}

      {message.quotedHtml ? (
        <div
          className="email-body print-body print-quoted"
          dangerouslySetInnerHTML={{ __html: message.quotedHtml }}
        />
      ) : null}

      {/* Named, not fetched. The files are not part of the paper, but a reader
          holding a printout needs to know the invoice was attached. */}
      {message.attachments.length > 0 ? (
        <p className="print-attachments">
          Attachments:{" "}
          {message.attachments.map((file) => file.filename).join(", ")}
        </p>
      ) : null}
    </article>
  );
}
